<template>
    <div
        class="app-editable-row"
        :class="{
            'app-editable-row--editing': editing,
            'app-editable-row--readonly': readonly,
            'app-editable-row--disabled': disabled,
            'app-editable-row--expanded': expanded,
            'app-editable-row--dense': dense,
            'app-editable-row--wrap': wrap
        }"
    >
        <div class="app-editable-row__row">
            <!-- Label -->
            <slot name="before-label" />
            <span v-if="label || findSlot('label')" class="app-editable-row__label">
                <label v-if="label">{{ label }}</label>
                <slot v-else-if="findSlot('label')" name="label" />
            </span>
            <slot name="after-label" />

            <!-- Info -->
            <app-tooltip v-if="info" :value="info" class="ml-1">
                <v-icon dense small color="info" style="margin-bottom: 0.1rem">
                    {{ getIcon('info', 'fad') }}
                </v-icon>
            </app-tooltip>

            <v-spacer class="mx-2" />

            <!-- Display -->
            <template v-if="saving">
                <app-progress indeterminate class="align-self-center" />
            </template>
            <template v-else>
                <slot name="before-display" />
                <span v-if="display || findSlot('display') || findSlot('default')" class="app-editable-row__display">
                    <template v-if="display">{{ display }}</template>
                    <slot v-else-if="findSlot('display')" name="display" />
                    <slot v-else-if="findSlot('default')" name="default" />
                </span>
                <span v-else-if="displayFallback" class="app-editable-row__display app-editable-row__display--fallback">
                    {{ displayFallback }}
                </span>
                <slot name="after-display" />
                <app-tooltip v-if="displayInfo" :value="displayInfo" class="ml-1">
                    <v-icon dense small color="info" style="margin-bottom: 0.1rem">
                        {{ getIcon('info', 'fad') }}
                    </v-icon>
                </app-tooltip>
            </template>

            <!-- Row Actions -->
            <span v-if="!readonly" class="app-editable-row__row-actions">
                <slot name="row-actions" />
                <transition v-if="!expanded && !hideEditButton" name="scroll-y-transition" mode="out-in">
                    <app-button
                        v-if="editing"
                        :icon="getIcon('cancel')"
                        small
                        icon-size="1rem"
                        :tooltip="disabled ? null : $t('cancel')"
                        :disabled="disabled || saving"
                        @click="onCancel"
                    />
                    <app-button
                        v-else
                        :icon="getIcon('edit')"
                        small
                        :tooltip="disabled ? null : $t('edit')"
                        :disabled="disabled || saving"
                        @click="onOpen"
                    />
                </transition>
            </span>
        </div>

        <div v-if="(editing || expanded) && findSlot('form')" class="app-editable-row__form">
            <slot name="form" v-bind="{ data, onInput, onUpdate, onSave, onClose, isDirty, saving }" />
        </div>
    </div>
</template>

<script>
import diff from '@/lib/helpers/diff';

export default {
    name: 'AppEditableRow',
    props: {
        value: {
            type: [String, Object, Boolean],
            default: null
        },
        presetValue: {
            type: [String, Object, Boolean],
            default: null
        },
        label: {
            type: String,
            default: null
        },
        info: {
            type: String,
            default: null
        },
        display: {
            type: String,
            default: null
        },
        displayFallback: {
            type: String,
            default: null
        },
        displayInfo: {
            type: String,
            default: null
        },
        readonly: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        hideEditButton: {
            type: Boolean,
            default: false
        },
        expanded: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        saving: {
            type: Boolean,
            default: false
        },
        wrap: {
            type: Boolean,
            default: false
        },
        // Prevent the object value from being stripped of reactivity
        unref: {
            type: Boolean,
            default: false
        },
        preventOpenOnEdit: {
            type: Boolean,
            default: false
        },
        onOpenFn: {
            type: Function,
            default: null
        }
    },
    data: () => ({
        editing: false,
        data: {},
        original: null
    }),
    computed: {
        isDirty() {
            if (typeof this.original === 'number') {
                return new String(this.original) !== new String(this.data);
            } else if (typeof this.original === 'object') {
                const differences = diff(this.original, this.data, {
                    removeNull: false,
                    removeEmptyStrings: false,
                    removeEmptyObjects: false
                });

                return Object.keys(differences).length > 0;
            }

            return this.original !== this.data;
        }
    },
    watch: {
        value(value) {
            if (typeof value === 'object' && !this.unref) {
                this.original = JSON.parse(JSON.stringify(value));

                if (!this.editing) {
                    this.data = JSON.parse(JSON.stringify(value));
                }
            } else {
                this.original = value;

                if (!this.editing) {
                    this.data = value;
                }
            }
        },
        presetValue: {
            immediate: true,
            handler(value) {
                if (this.value === undefined || this.value === null) {
                    this.data = value;
                }
            }
        },
        editing(editing) {
            this.$emit('editing', editing);
        }
    },
    methods: {
        setup() {
            if (typeof this.value === 'object' && !this.unref) {
                this.original = JSON.parse(JSON.stringify(this.value));
            } else {
                this.original = this.value;
            }

            if (this.value === undefined || this.value === null) {
                this.data = this.presetValue;
            } else {
                this.data = this.value;
            }
        },
        onCancel() {
            this.data = this.original;
            this.editing = false;
        },
        async onOpen() {
            if (this.onOpenFn) {
                await this.onOpenFn(this.data);
            }

            if (!this.preventOpenOnEdit) {
                this.editing = true;
            }
        },
        onClose() {
            this.editing = false;
        },
        onInput(data) {
            this.data = data;
        },
        onUpdate(values) {
            for (const [key, value] of Object.entries(values)) {
                this.data[`${key}`] = value;
            }
        },
        onSave(data, options = {}) {
            options = {
                closeOnSave: this.closeOnSave,
                ...options
            };

            this.data = data;
            this.$emit('input', this.data);

            if (options.closeOnSave) {
                this.editing = false;
            }
        }
    }
};
</script>

<style lang="scss">
.app-editable-row {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;

    &--dense {
        padding-top: 0.3rem;
        padding-bottom: 0.3rem;
        .app-editable-row__label {
            font-size: 0.85rem;
        }
        .app-editable-row__display {
            font-size: 0.875rem;
        }
    }

    &__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__label {
        flex-shrink: 0;
        font-size: 1rem;
        color: rgba(0, 0, 0, 0.6);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.4;
    }

    &__display {
        text-align: right;
        color: var(--v-primaryText-base);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 1rem;
        &--fallback {
            color: rgba(0, 0, 0, 0.6);
        }
    }

    &__row-actions {
        display: flex;
        align-items: center;
        margin-left: 0.5rem;
    }

    &__form {
        padding: 1rem;
        > *:last-child {
            margin-bottom: 0;
        }
    }

    &--wrap {
        .app-editable-row__row {
            align-items: first baseline;
        }
        .app-editable-row__label {
            white-space: wrap;
            flex-shrink: 1;
        }
        .app-editable-row__display {
            text-overflow: none;
            overflow: visible;
        }
    }

    &--disabled {
        .app-editable-row__label {
            opacity: 0.8;
        }
    }
}
</style>
